import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api, formatError } from "@/lib/api";
import { useAuth, hasRole } from "@/lib/auth";
import { toast } from "sonner";
import { RotateCcw } from "lucide-react";

const TYPE_COLORS = {
  receipt: "bg-success/10 text-success",
  issue: "bg-destructive/10 text-destructive",
  move: "bg-muted text-foreground",
  split: "bg-secondary/15 text-secondary",
  merge: "bg-secondary/15 text-secondary",
  adjust: "bg-warning/15 text-yellow-800",
  revert: "bg-muted text-foreground",
};

export default function Activity() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const [filters, setFilters] = useState({
    start: new Date(Date.now() - 7 * 24 * 3600 * 1000).toISOString().slice(0, 10),
    end: new Date().toISOString().slice(0, 10),
    type: "",
  });
  const { data, isLoading } = useQuery({
    queryKey: ["activity", filters],
    queryFn: async () => (await api.get("/activity", { params: {
      start: filters.start ? filters.start + "T00:00:00" : undefined,
      end: filters.end ? filters.end + "T23:59:59" : undefined,
      type: filters.type || undefined,
    } })).data,
  });

  const revert = async (group_id) => {
    const remark = window.prompt("Reason for revert?");
    if (remark === null) return;
    try {
      await api.post(`/activity/revert/${group_id}`, { remark });
      toast.success("Reverted");
      qc.invalidateQueries();
    } catch (e) { toast.error(formatError(e)); }
  };

  const canRevert = hasRole(user, "admin", "manager");

  // group by group_id
  const groups = new Map();
  (data || []).forEach(t => {
    if (!groups.has(t.group_id)) groups.set(t.group_id, { header: t, rows: [] });
    groups.get(t.group_id).rows.push(t);
  });

  return (
    <div className="space-y-6 animate-fade-in" data-testid="activity-log">
      <div>
        <div className="data-label">Ledger</div>
        <h1 className="font-display text-3xl sm:text-4xl font-bold">Activity Log</h1>
      </div>
      <div className="flex flex-wrap gap-3 items-end bg-white border border-border rounded-sm p-3">
        <label className="flex-1 min-w-[140px]">
          <span className="data-label">From</span>
          <input type="date" className="field-sm mt-1" value={filters.start}
                 onChange={(e) => setFilters(f => ({ ...f, start: e.target.value }))}
                 data-testid="activity-start" />
        </label>
        <label className="flex-1 min-w-[140px]">
          <span className="data-label">To</span>
          <input type="date" className="field-sm mt-1" value={filters.end}
                 onChange={(e) => setFilters(f => ({ ...f, end: e.target.value }))}
                 data-testid="activity-end" />
        </label>
        <label className="flex-1 min-w-[140px]">
          <span className="data-label">Type</span>
          <select className="field-sm mt-1" value={filters.type}
                  onChange={(e) => setFilters(f => ({ ...f, type: e.target.value }))}
                  data-testid="activity-type">
            <option value="">All</option>
            {Object.keys(TYPE_COLORS).map(t => <option key={t} value={t}>{t}</option>)}
          </select>
        </label>
      </div>

      {isLoading ? <div className="text-muted-foreground">Loading…</div> : (
        <div className="bg-white border border-border rounded-sm divide-y divide-border" data-testid="activity-list">
          {[...groups.values()].sort((a, b) => b.header.timestamp.localeCompare(a.header.timestamp)).map(({ header, rows }) => (
            <div key={header.group_id} className="p-4 space-y-2">
              <div className="flex items-center gap-3 flex-wrap">
                <span className={`badge-flat ${TYPE_COLORS[header.type] || "bg-muted"}`}>{header.type}</span>
                <span className="font-mono text-xs text-muted-foreground">
                  {new Date(header.timestamp).toLocaleString()}
                </span>
                <span className="text-sm">
                  by <span className="font-semibold">{header.user_name}</span>
                </span>
                {header.status === "reverted" && (
                  <span className="badge-flat bg-destructive/10 text-destructive">Reverted</span>
                )}
                {canRevert && header.status === "posted" && (
                  <button onClick={() => revert(header.group_id)}
                          className="ml-auto text-xs uppercase tracking-widest font-semibold text-primary flex items-center gap-1"
                          data-testid={`activity-revert-${header.group_id}`}>
                    <RotateCcw className="w-3 h-3" /> Revert
                  </button>
                )}
              </div>
              <ul className="pl-4 border-l-2 border-border space-y-1 text-sm">
                {rows.map(r => (
                  <li key={r.id} className="flex items-center gap-2 flex-wrap">
                    <span className="font-medium">{r.item_name}</span>
                    {r.container_code && <span className="container-code text-primary">{r.container_code}</span>}
                    {r.location_name && <span className="text-muted-foreground">@ {r.location_name}</span>}
                    {r.to_location_name && <span className="text-muted-foreground">→ {r.to_location_name}</span>}
                    {r.to_container_code && <span className="container-code text-secondary">→ {r.to_container_code}</span>}
                    <span className="font-mono ml-auto">{r.qty_change > 0 ? "+" : ""}{r.qty_change}</span>
                  </li>
                ))}
              </ul>
              {header.reverted_by_name && (
                <div className="text-xs text-muted-foreground">
                  Reverted by {header.reverted_by_name} on {new Date(header.reverted_at).toLocaleString()}
                </div>
              )}
            </div>
          ))}
          {groups.size === 0 && <div className="p-6 text-sm text-muted-foreground">No activity in this range.</div>}
        </div>
      )}
    </div>
  );
}
